import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Users, Plus, Pencil, Trash2, Coins, Clock, Bitcoin, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    tokens: 0,
    btc_wallet: '',
    unlimited_access_days: null
  });

  const [editForm, setEditForm] = useState({
    tokens: 0,
    btc_wallet: '',
    unlimited_access_days: null
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/transactions`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchTransactions()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/admin/users`,
        {
          ...newUser,
          unlimited_access_days: newUser.unlimited_access_days === 'none' ? null : parseInt(newUser.unlimited_access_days)
        },
        { withCredentials: true }
      );
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setNewUser({ email: '', password: '', tokens: 0, btc_wallet: '', unlimited_access_days: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      await axios.put(
        `${API_URL}/api/admin/users/${selectedUser.id}`,
        {
          tokens: editForm.tokens,
          btc_wallet: editForm.btc_wallet || null,
          unlimited_access_days: editForm.unlimited_access_days === 'none' ? 0 : 
            editForm.unlimited_access_days ? parseInt(editForm.unlimited_access_days) : null
        },
        { withCredentials: true }
      );
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      tokens: user.tokens || 0,
      btc_wallet: user.btc_wallet || '',
      unlimited_access_days: null
    });
    setEditDialogOpen(true);
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-white mt-4">Access Denied</h1>
            <p className="text-zinc-500 mt-2">You don't have permission to view this page.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Admin Dashboard
            </h1>
            <p className="text-zinc-500 mt-2">Manage users and monitor activity</p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-user-btn"
                className="h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                    data-testid="new-user-email"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                    data-testid="new-user-password"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Initial Tokens</Label>
                  <Input
                    type="number"
                    value={newUser.tokens}
                    onChange={(e) => setNewUser({ ...newUser, tokens: parseInt(e.target.value) || 0 })}
                    data-testid="new-user-tokens"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">BTC Wallet (optional)</Label>
                  <Input
                    type="text"
                    value={newUser.btc_wallet}
                    onChange={(e) => setNewUser({ ...newUser, btc_wallet: e.target.value })}
                    placeholder="bc1q..."
                    data-testid="new-user-btc"
                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Unlimited Access</Label>
                  <Select
                    value={newUser.unlimited_access_days || 'none'}
                    onValueChange={(v) => setNewUser({ ...newUser, unlimited_access_days: v })}
                  >
                    <SelectTrigger data-testid="new-user-unlimited" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="none">No unlimited access</SelectItem>
                      <SelectItem value="1">1 Day</SelectItem>
                      <SelectItem value="2">2 Days</SelectItem>
                      <SelectItem value="3">3 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  data-testid="submit-create-user"
                  className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                >
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Users</p>
                <p className="text-2xl font-bold text-white font-mono" data-testid="total-users">
                  {users.length}
                </p>
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Transactions</p>
                <p className="text-2xl font-bold text-white font-mono" data-testid="total-transactions">
                  {transactions.length}
                </p>
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Active Unlimited</p>
                <p className="text-2xl font-bold text-white font-mono" data-testid="active-unlimited">
                  {users.filter(u => u.unlimited_access_until && new Date(u.unlimited_access_until) > new Date()).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="glass rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="font-heading text-xl font-semibold text-white">Users</h2>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Role</TableHead>
                  <TableHead className="text-zinc-400">Tokens</TableHead>
                  <TableHead className="text-zinc-400">BTC Wallet</TableHead>
                  <TableHead className="text-zinc-400">Unlimited Until</TableHead>
                  <TableHead className="text-zinc-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="border-white/10" data-testid={`user-row-${user.id}`}>
                      <TableCell className="text-white font-mono">{user.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          user.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {user.role}
                        </span>
                      </TableCell>
                      <TableCell className="text-amber-400 font-mono">{user.tokens?.toLocaleString()}</TableCell>
                      <TableCell className="text-zinc-400 font-mono text-xs max-w-[150px] truncate">
                        {user.btc_wallet || '-'}
                      </TableCell>
                      <TableCell className="text-zinc-400">
                        {user.unlimited_access_until && new Date(user.unlimited_access_until) > new Date()
                          ? new Date(user.unlimited_access_until).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                            className="text-zinc-400 hover:text-white"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(user.id)}
                              data-testid={`delete-user-${user.id}`}
                              className="text-zinc-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Edit User: {selectedUser?.email}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tokens</Label>
                <Input
                  type="number"
                  value={editForm.tokens}
                  onChange={(e) => setEditForm({ ...editForm, tokens: parseInt(e.target.value) || 0 })}
                  data-testid="edit-user-tokens"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">BTC Wallet</Label>
                <Input
                  type="text"
                  value={editForm.btc_wallet}
                  onChange={(e) => setEditForm({ ...editForm, btc_wallet: e.target.value })}
                  placeholder="bc1q..."
                  data-testid="edit-user-btc"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Grant Unlimited Access</Label>
                <Select
                  value={editForm.unlimited_access_days || 'none'}
                  onValueChange={(v) => setEditForm({ ...editForm, unlimited_access_days: v })}
                >
                  <SelectTrigger data-testid="edit-user-unlimited" className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="none">No change / Remove</SelectItem>
                    <SelectItem value="1">1 Day</SelectItem>
                    <SelectItem value="2">2 Days</SelectItem>
                    <SelectItem value="3">3 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                data-testid="submit-edit-user"
                className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                Update User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Admin;
